import Image, { getImageProps, type ImageProps } from "next/image"

import type { SiteBanner } from "@/lib/banners"

type BannerImageProps = Pick<
  ImageProps,
  "alt" | "className" | "sizes" | "priority" | "loading"
> & {
  banner: Pick<SiteBanner, "image" | "mobileImage">
  mobileSizes: string
}

function isRemoteImage(src: string | undefined): src is string {
  if (!src) return false
  try {
    const url = new URL(src)
    return url.protocol === "https:" || url.protocol === "http:"
  } catch {
    return false
  }
}

export default function BannerImage({
  banner,
  mobileSizes,
  ...imageProps
}: BannerImageProps) {
  const desktopSrc = banner.image.trim()
  const mobileSrc = banner.mobileImage?.trim()
  if (!isRemoteImage(mobileSrc)) {
    return <Image {...imageProps} src={desktopSrc} fill />
  }

  const { props: desktopProps } = getImageProps({
    ...imageProps,
    src: desktopSrc,
    fill: true,
    fetchPriority: imageProps.priority ? "high" : undefined,
    loading: imageProps.priority ? "eager" : imageProps.loading,
  })
  const { props: mobileProps } = getImageProps({
    ...imageProps,
    src: mobileSrc,
    fill: true,
    sizes: mobileSizes,
  })

  return (
    <picture>
      <source
        media="(max-width: 639px)"
        srcSet={mobileProps.srcSet ?? mobileProps.src}
        sizes={mobileProps.sizes}
      />
      {/* getImageProps optimizes both sources; picture selects one before loading. */}
      <img {...desktopProps} alt={imageProps.alt} />
    </picture>
  )
}
